import { useCallback, useLayoutEffect } from 'react';
import type { MessengerMessage } from '@/api/client';
import { isMessengerViewportAtBottom } from '@/components/Messenger/shared/viewport/messengerViewportScroll';
import {
  directThreadLoadInitial,
  directThreadLoadOlder,
  directThreadLoadNewer,
  directThreadJumpToTailWindow,
  directThreadMarkReadToBottom,
  directThreadRetryMessage,
  directThreadSendForwardedMessagesBatch,
  directThreadSendOwnAndForwardedAtomic,
  directThreadSendMessage,
  directThreadSendWithLocalImageFiles,
  directThreadSetError,
  type DirectThreadAtomicOwnPayload,
  type DirectThreadForwardSendPayload,
} from '@/components/Messenger/cache/directThreadCacheStore';
import { useDirectThreadMessages } from '@/hooks/useDirectThreadMessages';
import { useDirectThreadMeta } from '@/hooks/useDirectThreadMeta';

/** Действия треда без подписки на messages/meta — для изолированных панелей composer. */
export function useDirectThreadActions(threadId: string | null, currentUserId: string | null) {
  useLayoutEffect(() => {
    if (!threadId) return;
    void directThreadLoadInitial(threadId);
  }, [threadId]);

  const loadOlder = useCallback(() => {
    if (!threadId) return;
    void directThreadLoadOlder(threadId);
  }, [threadId]);

  const loadNewer = useCallback(() => {
    if (!threadId) return;
    void directThreadLoadNewer(threadId);
  }, [threadId]);

  const jumpToTailWindow = useCallback(async () => {
    if (!threadId) return;
    await directThreadJumpToTailWindow(threadId);
  }, [threadId]);

  const sendMessage = useCallback(
    async (data: {
      body?: string;
      file_url?: string;
      file_name?: string;
      file_size?: number;
      mime_type?: string;
      image_width?: number;
      image_height?: number;
      context_attachments?: unknown;
      reply_to_id?: string;
      forwarded_from_message_id?: string;
      /** Локальные файлы: оптимистичный бабл → загрузка → POST (отдельно от агентского чата). */
      localImageFiles?: File[];
    }) => {
      if (!threadId) return;
      if (!currentUserId) {
        const err = new Error('Пользователь не загружен. Обновите страницу.');
        directThreadSetError(threadId, err.message);
        throw err;
      }
      const { localImageFiles, ...rest } = data;
      if (localImageFiles?.length) {
        return directThreadSendWithLocalImageFiles(threadId, currentUserId, {
          body: rest.body,
          context_attachments: rest.context_attachments,
          imageFiles: localImageFiles,
          reply_to_id: rest.reply_to_id,
        });
      }
      return directThreadSendMessage(threadId, currentUserId, rest);
    },
    [threadId, currentUserId]
  );

  const sendOwnAndForwardedAtomic = useCallback(
    async (args: {
      own?: DirectThreadAtomicOwnPayload;
      forwards: Array<{
        apiPayload: DirectThreadForwardSendPayload;
        forwarded_from_sender_name: string;
      }>;
    }) => {
      if (!threadId) return [];
      if (!currentUserId) {
        const err = new Error('Пользователь не загружен. Обновите страницу.');
        directThreadSetError(threadId, err.message);
        throw err;
      }
      return directThreadSendOwnAndForwardedAtomic(threadId, currentUserId, args);
    },
    [threadId, currentUserId]
  );

  const sendForwardedMessagesBatch = useCallback(
    async (
      items: Array<{
        apiPayload: DirectThreadForwardSendPayload;
        forwarded_from_sender_name: string;
      }>
    ): Promise<MessengerMessage | undefined> => {
      if (!threadId) return;
      if (!currentUserId) {
        const err = new Error('Пользователь не загружен. Обновите страницу.');
        directThreadSetError(threadId, err.message);
        throw err;
      }
      return directThreadSendForwardedMessagesBatch(threadId, currentUserId, items);
    },
    [threadId, currentUserId]
  );

  const markReadToBottom = useCallback(
    (messageId: string) => {
      if (!threadId) return;
      directThreadMarkReadToBottom(threadId, messageId);
    },
    [threadId]
  );

  const isAtBottom = useCallback((scrollEl: HTMLElement | null): boolean => {
    return isMessengerViewportAtBottom(scrollEl);
  }, []);

  const retryMessage = useCallback(
    (optimisticId: string) => {
      if (!threadId || !currentUserId) return;
      void directThreadRetryMessage(threadId, currentUserId, optimisticId);
    },
    [threadId, currentUserId]
  );

  return {
    loadOlder,
    loadNewer,
    jumpToTailWindow,
    sendMessage,
    sendOwnAndForwardedAtomic,
    sendForwardedMessagesBatch,
    markReadToBottom,
    isAtBottom,
    retryMessage,
    refetch: () => {
      if (threadId) return directThreadLoadInitial(threadId, { force: true });
    },
  };
}

export function useDirectThread(threadId: string | null, currentUserId: string | null) {
  const content = useDirectThreadMessages(threadId);
  const meta = useDirectThreadMeta(threadId);
  const actions = useDirectThreadActions(threadId, currentUserId);

  return {
    messages: content.messages,
    pinnedMessages: content.pinnedMessages,
    hasMore: content.hasMoreOlder,
    hasMoreOlder: content.hasMoreOlder,
    hasMoreNewer: content.hasMoreNewer,
    lastReadMessageId: meta.lastReadMessageId,
    loading: meta.loading,
    loadingOlder: meta.loadingOlder,
    loadingNewer: meta.loadingNewer,
    error: meta.error,
    ...actions,
  };
}
